import {
  createRootRoute,
  Outlet,
  useNavigate,
  useLocation,
} from '@tanstack/react-router';
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools';
import { Suspense } from 'react';
import { useAuth } from '../hooks/use-auth';
import { useEffect } from 'react';
import { Header, Footer, ScreenLoader, EnvironmentBanner } from '@/components';
import {
  isPublicRoute,
  isUserAuthorizedForRoute,
} from '../config/route-access.config';
import { Role } from '../constants/roles.constants';

function RootComponent() {
  const { user, isAuthenticated, isLoading } = useAuth();

  const navigate = useNavigate();
  const location = useLocation();

  const currentPath = location.pathname;
  const fullPath = location.searchStr
    ? currentPath + location.searchStr
    : currentPath;
  const isPublic = isPublicRoute(currentPath);
  const isAuthorized = isUserAuthorizedForRoute(
    currentPath,
    user?.role as Role,
  );

  useEffect(() => {
    // Skip redirects while loading
    if (isLoading) return;

    // Case 1: Unauthenticated user
    if (!isAuthenticated) {
      // Allow public routes
      if (isPublic) return;
      // Redirect to login for non-public routes with redirect URL
      navigate({
        to: '/login',
        search: { 'redirect-url': fullPath },
        replace: true,
      });

      return;
    }

    // Case 2: Authenticated but still on a temporary password. Nothing else is
    // reachable until they set their own — the server enforces this independently, so
    // this redirect is convenience rather than security.
    if (user?.mustSetPassword) {
      if (currentPath !== '/set-password') {
        navigate({ to: '/set-password', replace: true });
      }
      return;
    }

    // Someone who has already set a password has no business on that screen.
    if (currentPath === '/set-password') {
      navigate({ to: '/dashboard', replace: true });
      return;
    }

    // Case 3: Authenticated user on root path
    if (currentPath === '/') {
      navigate({ to: '/dashboard' });
      return;
    }

    // Case 4: Authenticated user on login (public route)
    if (isPublic && currentPath === '/login') {
      navigate({ to: '/dashboard' });
      return;
    }

    // Case 5: Authenticated user trying to access protected route without authorization
    if (!isPublic && !isAuthorized) {
      navigate({ to: '/dashboard' });
      return;
    }
  }, [
    isAuthenticated,
    isLoading,
    user?.mustSetPassword,
    navigate,
    currentPath,
    isPublic,
    isAuthorized,
    fullPath,
    location.searchStr,
  ]);

  if (isLoading) {
    return <ScreenLoader loadingMessage="Loading..." />;
  }

  if (!isAuthenticated) {
    return (
      <>
        <EnvironmentBanner />
        <div className="vh-100 d-flex align-items-center justify-content-center auth-page">
          <div className="w-100 auth-card">
            <Outlet />
          </div>
          {import.meta.env.DEV && <TanStackRouterDevtools />}
        </div>
      </>
    );
  }

  return (
    <div className="default-layout-container">
      <EnvironmentBanner />
      <Header />
      <div className="default-layout-body">
        <Suspense fallback={<ScreenLoader />}>
          <Outlet />
        </Suspense>
      </div>
      <Footer />
      {import.meta.env.DEV && <TanStackRouterDevtools />}
    </div>
  );
}

export const Route = createRootRoute({
  component: RootComponent,
});
